import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { BellRing, ChevronRight, CheckCircle2, RefreshCcw } from "lucide-react";

const ERNST_KLEUR = {
  danger: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  info: "bg-blue-50 text-blue-700 border-blue-200",
};

/* Actielijst: alle cross-beheer signalen gebundeld. Alleen voor managers/directie
   (backend geeft 403 voor anderen → widget verbergt zichzelf). */
export function SignaleringenWidget() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [zichtbaar, setZichtbaar] = useState(true);
  const [busy, setBusy] = useState(false);
  const [alles, setAlles] = useState(false);

  const laad = async () => {
    setBusy(true);
    try {
      const r = await api.get("/signaleringen");
      setData(r.data);
    } catch {
      setZichtbaar(false);
    }
    setBusy(false);
  };
  useEffect(() => { laad(); }, []);

  if (!zichtbaar || !data) return null;
  const orde = { danger: 0, warning: 1, info: 2 };
  const actief = (data.signaleringen || []).filter((s) => s.aantal > 0).sort((a, b) => (orde[a.ernst] ?? 2) - (orde[b.ernst] ?? 2) || b.aantal - a.aantal);
  const getoond = alles ? actief : actief.slice(0, 6);
  const verborgen = actief.length - getoond.length;

  return (
    <Card className="p-4" data-testid="signaleringen-widget">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold text-slate-800 flex items-center gap-2 text-sm sm:text-base">
          <BellRing className="h-4 w-4 text-[hsl(var(--brand-orange))]" />
          Signaleringen
          {data.totaal_acties > 0 && (
            <span className="text-xs bg-red-100 text-red-700 rounded-full px-2 py-0.5 font-bold"
                  data-testid="signaleringen-totaal">
              {actief.filter((s) => s.ernst === "danger").length} urgent · {actief.length} totaal
            </span>
          )}
        </h2>
        <button onClick={laad} disabled={busy} title="Vernieuwen"
                className="text-slate-400 hover:text-slate-600" data-testid="signaleringen-refresh">
          <RefreshCcw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>
      {actief.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-700 py-2" data-testid="signaleringen-schoon">
          <CheckCircle2 className="h-4 w-4" /> Geen openstaande signalen — alles binnen de normen.
        </div>
      ) : (
        <div className="divide-y">
          {getoond.map((s) => (
            <button key={s.slug + s.label} onClick={() => nav(s.url)}
                    className="w-full flex items-center justify-between gap-2 py-2 text-left hover:bg-slate-50 rounded px-1 transition-colors"
                    data-testid={`signaal-${s.slug}`}>
              <span className="text-sm text-slate-700 min-w-0 truncate">{s.label}</span>
              <span className="flex items-center gap-1.5 shrink-0">
                <span className={`text-xs font-bold border rounded-full px-2 py-0.5 tabular-nums ${ERNST_KLEUR[s.ernst] || ERNST_KLEUR.info}`}>
                  {s.aantal.toLocaleString("nl-NL")}
                </span>
                <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
              </span>
            </button>
          ))}
          {(verborgen > 0 || alles) && actief.length > 6 && (
            <button onClick={() => setAlles(!alles)} className="w-full py-2 text-xs font-semibold text-[hsl(var(--brand-blue))] hover:underline text-left px-1" data-testid="signaleringen-toon-alles">
              {alles ? "Minder tonen" : `Toon alle ${actief.length} signalen (+${verborgen})`}
            </button>
          )}
        </div>
      )}
      <div className="text-[10px] text-slate-400 mt-2">
        Gesorteerd op urgentie. Normen en aan/uit per signaal instelbaar via het Beheer-tandwiel op elke module-pagina.
      </div>
    </Card>
  );
}

export default SignaleringenWidget;
